import {Fragment, useMemo} from 'react';
import {StaticLandingTodayUpMoreFetcher} from '../../../../common/fetchers/StaticLandingTodayUpMoreFetcher';
import {t, tt} from '../../../../common/getTranslations';
import {isGreaterThanZero} from '../../../../common/isGreaterThanZero';
import {LogTabType} from '../../../../common/LogTabType.enum';
import {TabPositionSticky} from '../../../../common/presentational/TabPositionSticky';
import {requestContext} from '../../../../common/requestContext';
import {formatWithCommas} from '../../../../common/shared/utils/formatWithCommas';
import {getValidChildren} from '../../../../common/shared/utils/getValidChildren';
import {Divider} from '../../../../design-system/atoms/Divider/Divider';
import {BelowDefaultTab} from '../../../../design-system/organisms/BelowDefaultTab/BelowDefaultTab';
import {MainTab} from '../../../../design-system/organisms/MainTab/MainTab';
import {NoItem} from '../../../../design-system/organisms/NoItem/NoItem';
import {SubTab} from '../../../../design-system/organisms/SubTab/SubTab';
import {SubtabItemActiveColorScheme} from '../../../../design-system/organisms/SubTabItem/constants';
import {SubTabItemType} from '../../../../design-system/organisms/SubTabItem/SubTabItemType.interface';
import {EMPTY_LANDING_PRODUCT_TEXT_KEY} from '../../../../domain/landing/constant';
import {StaticLandingTodayUpSection} from '../../../../graphql/generated/types';
import {aggregateAndSendLog} from '../../../../logging/aggregateAndSendLog';
import {putTiaraObjectProps} from '../../../../logging/putTiaraObjectProps';
import {STATIC_LANDING_TODAY_UP_CATEGORY_LIST} from '../../../../staticLanding/todayUp/constants';
import {theme} from '../../../../theme';
import {CommonSectionProps} from '../../type';

export function StaticLandingTodayUp({section, children}: CommonSectionProps) {
  const {isMobile, isWebView} = requestContext.requestInfo.display;
  const validChildren = getValidChildren(children);

  const staticLandingTodayUpSection = section as Omit<
    StaticLandingTodayUpSection,
    'groups'
  >;
  const {
    param: {categoryUid, subcategory},
    totalCount,
    subcategoryList: _subcategoryList,
  } = staticLandingTodayUpSection;

  const subcategoryList: SubTabItemType[] = useMemo(() => {
    return _subcategoryList.map(({name, param}) => ({
      name,
      param,
      href: `/landing/today-up/${categoryUid}/${param}`,
    }));
  }, [_subcategoryList, categoryUid]);

  const currentCategoryName = useMemo(() => {
    const currentCategory = STATIC_LANDING_TODAY_UP_CATEGORY_LIST.find(
      categoryInfo => categoryInfo.id === categoryUid
    );

    return tt(currentCategory);
  }, [categoryUid]);
  const currentSubcategoryName = subcategory?.name;

  return (
    <div
      className="flex flex-col w-full mb-15pxr grow"
      {...putTiaraObjectProps({
        customProps: {
          page_category_filter: currentCategoryName,
          page_genre_filter: currentSubcategoryName,
        },
        click: {
          layer1: '섹션',
        },
      })}
    >
      <TabPositionSticky
        depth={1}
        disabled={!isMobile}
        isMobile={isMobile}
        isWebView={isWebView}
      >
        <MainTab
          isMobile={isMobile}
          title={t('landing_up')}
          tabItems={STATIC_LANDING_TODAY_UP_CATEGORY_LIST}
          currentTabItemId={categoryUid}
          className={!isMobile ? 'px-15pxr' : undefined}
          logTabType={LogTabType.Category}
          showBorder={true}
        />
      </TabPositionSticky>
      {subcategory && (
        <TabPositionSticky
          depth={2}
          disabled={!isMobile}
          isMobile={isMobile}
          isWebView={isWebView}
        >
          <SubTab
            tabItems={subcategoryList}
            currentTabItemParam={subcategory.param}
            activeColorScheme={
              !isMobile ? SubtabItemActiveColorScheme['2nd'] : undefined
            }
            onClickTab={(clickedTabItemId, e) => {
              aggregateAndSendLog({
                targetElem: e?.target as HTMLElement,
                actionName: '장르_클릭',
                actionType: 'Event',
                click: {
                  copy: subcategoryList.find(
                    subcategory => subcategory.param === clickedTabItemId
                  )?.name,
                },
                customProps: {
                  user_action: 'click',
                },
              });
            }}
            logTabType={LogTabType.Genre}
            {...putTiaraObjectProps({
              click: {
                layer1: '서브탭2',
              },
            })}
          />
        </TabPositionSticky>
      )}
      {!!totalCount && (
        <BelowDefaultTab className="mb-5pxr px-15pxr">
          <BelowDefaultTab.TextLabel>
            {formatWithCommas(totalCount)}개
          </BelowDefaultTab.TextLabel>
        </BelowDefaultTab>
      )}
      {isGreaterThanZero(totalCount) && (
        <StaticLandingTodayUpMoreFetcher section={staticLandingTodayUpSection}>
          <div className="flex flex-col grow py-5pxr">
            {validChildren.map((validChild, idx) => {
              return (
                <Fragment key={idx}>
                  {validChild}
                  {idx !== validChildren.length - 1 && (
                    <Divider
                      color={theme.colors['theme-transparent-20']}
                      className="my-24pxr mx-15pxr"
                    />
                  )}
                </Fragment>
              );
            })}
          </div>
        </StaticLandingTodayUpMoreFetcher>
      )}
      {!isGreaterThanZero(totalCount) && (
        <NoItem isMobile={isMobile}>{t(EMPTY_LANDING_PRODUCT_TEXT_KEY)}</NoItem>
      )}
    </div>
  );
}
